import React, { useEffect, useState } from "react";
import "./styles/main.scss";

import "@formatjs/intl-pluralrules/polyfill";
import "@formatjs/intl-pluralrules/locale-data/en";
import "@formatjs/intl-pluralrules/locale-data/pl";

import "react-toastify/dist/ReactToastify.css";

import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { Redirect } from "react-router";

import axios from "axios";
import {
  getTranslations,
  getTranslationsForMember,
  setPdfReadyModalData,
  identify,
  isLoggedIn,
  setActiveWorkspaceUuid,
  setWebSocket,
} from "src/redux/main/mainActions";
import Project from "./app/project/Project";
import Homepage from "./app/homepage/Homepage";
import SingleProject from "./app/project/SingleProject/SingleProject";
import MyTasks from "./app/MyTasks/MyTasks";
import LoginPage from "./app/LoginPage/LoginPage";
import WorkspaceCatcher from "./app/logic-components/WorkspaceCatcher";
import OnboardingPage from "./app/OnboardingPage/OnboardingPage";
import CreatorDiscoveryTool from "./app/pages/CreatorDiscoveryTool/CreatorDiscoveryTool";
import CreatorShowcase from "./app/CreatorShowcase/CreatorShowcase";
import { ReportGenerator } from "./app/ReportGenerator/ReportGenerator";
import FinanceReporting from "./app/FinanceReporting/FinanceReporting";
import MessageListener from "./MessageListener";
import { Activity } from "./app/components/Activity/Activity";
import StatusBar from "./app/components/StatusBar/StatusBar";
import { API_URLS } from "./utils/API_URLS";
import {
  customEqual,
  getWorkspaceUuidFromCurrentUrl,
  isCurrentUrlRequiresAuthorization,
  showErrorToast,
  urlIncludes,
} from "./utils/methods";
import { getShowcaseSettingByKey } from "./app/CreatorShowcase/utils";
import { AccessLinkPage } from "./app/pages/AccessLinkPage/AccessLinkPage";
import { TrialPage } from "./app/TrialPage/TrialPage";
import { showToast } from "./app/methods/showToast";
import IDHFormattedMessage from "./app/components/IDHFormattedMessage/IDHFormattedMessage";
import { SearchParamWatcher } from "./SearchParamWatcher";
import { RecruitmentFormAccessLinkPage } from "./app/pages/AccessLinkPage/RecruitmentFormAccessLinkPage";
import { CouponGroups } from "./app/CouponGroups/CouponGroups";
import Dictionary from "./app/Dictionary/Dictionary";
import CreatorDatabase from "./app/CreatorDatabase/CreatorDatabase";
import { WorkspacePermissionsEnum } from "./utils/PermissionsEnums";
import { CredibilityToolPage } from "./app/CredibilityTool/CredibilityToolPage";
import CredibilityToolListPage from "./app/CredibilityTool/CredibilityToolListPage";
import { HashtagLiveSearch } from "./app/HashtagLiveSearch/HashtagLiveSearch";
import { RootState } from "./redux/reducers";
import useNetworkStatus from "./hooks/useNetworkStatus";
import WsProjectReportShowcasePdfReadyModal from "./app/modals/WsProjectReportShowcasePdfReadyModal/WsProjectReportShowcasePdfReadyModal";
import WorkspaceTasksSummary from "./app/pages/WorkspaceTasksSummary/WorkspaceTasksSummary";
import { getProjectAutocompleteList, getSubscriptionPlan } from "./redux";
import DemoModeInfoBar, {
  DEMO_MODE_INFO_BAR_PADDING,
} from "./app/components/DemoModeInfoBar/DemoModeInfoBar";
import Providers from "./providers/Providers";
import { ConfirmRegistrationPage } from "./app/ConfirmRegistrationPage/ConfirmRegistrationPage";
import { ConfirmPaidSubscriptionPage } from "./app/ConfirmPaidSubscriptionPage/ConfirmPaidSubscriptionPage";
import NoActiveSubscriptionPage from "./app/NoActiveSubscriptionPage/NoActiveSubscriptionPage";
import { WsMemberErrorStatus } from "./utils/wsMemberEnums";
import SubscriptionCheckout from "./app/subscription/screens/SubscriptionCheckout/SubscriptionCheckout";
import SelectSubscriptionPlan from "./app/subscription/screens/SelectPlan/SelectPlan";
import UnlockAddons from "./app/subscription/screens/UnlockAddons/UnlockAddons";
import useCloseOnEscape from "./hooks/useCloseOnEscape";
import CampaignCreatorSearch from "./app/pages/CampaignCreatorSearch/CampaignCreatorSearch";
import CreateNewProjectPane from "./app/modals/CreateNewProjectPane/CreateNewProjectPane";
import OutsideClickWarningModal from "./app/components/OutsideClickWarningModal/OutsideClickWarningModal";
import { getAllSubscriptionEntitlements } from "./utils/getAllSubscriptionEntitlements";
import WorkspaceErrorBoundary from "src/app/components/WorkspaceErrorBoundary/WorkspaceErrorBoundary";
import { AccessDenied } from "./app/AccessDenied/AccessDenied";
import SearchHistoryPane from "./app/project/SingleProject/CreatorSearchTable/components/SearchHistoryPane/SearchHistoryPane";
import WorkspaceLayout from "./WorkspaceLayout";
import RecruitmentFormRedirect from "./RecruitmentFormRedirect";
import AIChat from "./app/AIChat/AIChat";
import { SocialInsights } from "./app/pages/SocialInsights/SocialInsights";
import AISuggests from "./app/components/AISuggests/AISuggests";

export const overlaysRef = React.createRef<any>();

function App() {
  const [translationsDownloaded, setTranslationsDownloaded] = useState(false);

  useNetworkStatus();
  useCloseOnEscape();

  const wsProjectReportShowcasePdfReadyModalData = useSelector(
    (state: RootState) =>
      state.mainReducer.wsProjectReportShowcasePdfReadyModalData,
  );

  const identity = useSelector(
    (state: RootState) => state.mainReducer.identity,
  );
  const notifications = useSelector(
    (state: RootState) => state.mainReducer.notifications,
    customEqual,
  );
  const websocket = useSelector(
    (state: RootState) => state.mainReducer.websocket,
  );

  const workspaceUuid = getWorkspaceUuidFromCurrentUrl();
  const showcaseWorkspaceUuid = urlIncludes("/creator-showcase")
    ? getShowcaseSettingByKey("workspaceId")
    : null;

  const excludedPathnames = ["", "creator-showcase"];

  const activeWorkspaceUuid =
    workspaceUuid && !excludedPathnames.includes(workspaceUuid)
      ? workspaceUuid
      : null;
  const identityWorkspaceUuid = activeWorkspaceUuid || showcaseWorkspaceUuid;

  const dispatch = useDispatch();

  const showOnboarding = identity?.errors?.includes(
    WsMemberErrorStatus.Onboarding,
  );
  const showNoActiveSubscriptionPage = identity?.errors?.includes(
    WsMemberErrorStatus.NoActiveSubscription,
  );

  const urlSearchParams = new URLSearchParams(window.location.search);
  const urlParamsCustomLocale = urlSearchParams.get("locale");

  useEffect(() => {
    dispatch(getTranslations(urlParamsCustomLocale));
  }, [urlParamsCustomLocale, dispatch]);

  useEffect(() => {
    if (activeWorkspaceUuid) {
      dispatch(setActiveWorkspaceUuid(activeWorkspaceUuid));
      dispatch(getProjectAutocompleteList(activeWorkspaceUuid));
    }
  }, [activeWorkspaceUuid, dispatch]);

  useEffect(() => {
    if (identity?.id && !translationsDownloaded) {
      dispatch(getTranslationsForMember());
      setTranslationsDownloaded(true);
    }
  }, [identity]);

  // websocket handling has been moved to separate component - WebsocketListener
  useEffect(() => {
    if (!isCurrentUrlRequiresAuthorization()) {
      return;
    }

    if (identityWorkspaceUuid) {
      dispatch(identify(identityWorkspaceUuid));
    } else {
      axios
        .get(API_URLS.getLastActiveWorkspaceUuid)
        .then((result) => {
          if (result?.data?.content?.lastActiveWorkspaceUuid) {
            dispatch(identify(result.data.content.lastActiveWorkspaceUuid));
          } else {
            dispatch(isLoggedIn());
          }
        })
        .catch((error) => {
          console.error(error);
          showErrorToast();
        });
    }
  }, [identityWorkspaceUuid]);

  useEffect(() => {
    if (!isCurrentUrlRequiresAuthorization()) {
      return;
    }

    if (
      !identity?.id &&
      window.location.pathname === "/workspace/magic-link-expired"
    ) {
      showToast(
        "info",
        <IDHFormattedMessage
          id="ws_magic_link_expired_title"
          defaultMessage="Info"
        />,
        <IDHFormattedMessage
          id="ws_magic_link_expired_message"
          defaultMessage="Login link expired"
        />,
      );
    }

    if (showOnboarding && window.location.pathname !== "/workspace/") {
      window.location.pathname = "/workspace/";
    }
  }, [identity, window.location.pathname]);

  useEffect(() => {
    if (
      activeWorkspaceUuid &&
      identity.id &&
      identity.websocketChallenge &&
      !websocket
    ) {
      dispatch(
        setWebSocket(
          activeWorkspaceUuid,
          identity.id,
          identity.websocketChallenge,
        ),
      );
    }
  }, [identity, activeWorkspaceUuid]);

  useEffect(() => {
    if (identity?.subscriptionData && activeWorkspaceUuid) {
      dispatch(getSubscriptionPlan(activeWorkspaceUuid));
      getAllSubscriptionEntitlements(dispatch, activeWorkspaceUuid);
    }
  }, [identity.subscriptionData, activeWorkspaceUuid, dispatch]);

  const hasAccessToCreatorDatabase = identity?.permissions?.workspace?.includes(
    WorkspacePermissionsEnum.CREATOR_DATABASE,
  );
  const hasAccessToDictionaries = identity?.permissions?.workspace?.includes(
    WorkspacePermissionsEnum.DICTIONARIES,
  );
  const hasAccessToCreatorDiscovery =
    identity?.permissions?.workspace?.includes(
      WorkspacePermissionsEnum.CREATOR_DISCOVERY,
    );
  const hasAccessToFinanceReporting =
    identity?.permissions?.workspace?.includes(
      WorkspacePermissionsEnum.FINANCE_REPORTING,
    );
  const canConfigureFinanceReporting =
    identity?.permissions?.workspace?.includes(
      WorkspacePermissionsEnum.FINANCE_REPORTING_CONFIGURATION,
    ) ?? false;

  const calculatePaddingToOffsetNotifications = () => {
    return (notifications ? notifications.length : 0) * 44;
  };

  const getPaddingToOffsetDemoModeInfoBar = () => {
    return identity?.isDemoWorkspace ? DEMO_MODE_INFO_BAR_PADDING : 0;
  };

  const getMarginLeft = () => {
    if (
      activeWorkspaceUuid &&
      !window.location.pathname.includes("/subscription/")
    ) {
      return 72;
    }
    return 0;
  };

  const renderStartingPage = () => {
    if (showNoActiveSubscriptionPage) return NoActiveSubscriptionPage;
    if (showOnboarding) return OnboardingPage;
    return LoginPage;
  };

  const renderDemoModeBar = () => {
    const DEMO_MODE_PAGE_BLACKLIST = ["creator-showcase", "report-generator"];

    if (
      DEMO_MODE_PAGE_BLACKLIST.some((item) =>
        window.location.pathname.includes(item),
      )
    )
      return null;

    if (identity?.isDemoWorkspace) {
      return <DemoModeInfoBar />;
    }
  };

  return (
    <div className="App">
      <Router>
        <Providers>
          <>
            <div className="sticky-bars">
              {renderDemoModeBar()}
              <StatusBar />
            </div>
            <main
              className="App__main-container"
              style={{
                marginLeft: getMarginLeft(),
                paddingTop:
                  calculatePaddingToOffsetNotifications() +
                  getPaddingToOffsetDemoModeInfoBar(),
              }}
            >
              <Switch>
                <Route
                  path="/workspace/access/:taskUuid/:type/:metafieldUuid/:token"
                  component={AccessLinkPage}
                />
                <Route
                  path="/workspace/recruitment-form/:extensionUuid/:token"
                  component={RecruitmentFormRedirect}
                />
                <Route
                  path="/workspace/submission-form/:extensionUuid/:token"
                  component={RecruitmentFormAccessLinkPage}
                />
                <Route
                  exact
                  path="/workspace/"
                  component={renderStartingPage()}
                />

                <Route
                  exact
                  path="/workspace/magic-link-expired"
                  component={LoginPage}
                />

                <Route exact path="/workspace/lite" component={TrialPage} />

                <Route
                  exact
                  path="/workspace/subscription"
                  component={TrialPage}
                />

                <Route
                  path="/workspace/confirm-registration"
                  component={ConfirmRegistrationPage}
                />

                {/* SUBSCRIPTION PAGE */}
                <Route
                  path="/workspace/:workspaceUuid/subscription/select-plan"
                  component={SelectSubscriptionPlan}
                />

                <Route
                  path="/workspace/:workspaceUuid/subscription/checkout/:planId"
                  component={SubscriptionCheckout}
                />

                <Route
                  path="/workspace/:workspaceUuid/subscription/unlock-addons"
                  component={UnlockAddons}
                />

                <Route
                  path="/workspace/confirm-paid-registration"
                  component={ConfirmPaidSubscriptionPage}
                />

                <Route
                  path="/workspace/credibility-tool/:userUuid"
                  component={CredibilityToolListPage}
                />

                <Route
                  exact
                  path="/workspace/credibility-tool"
                  component={CredibilityToolPage}
                />
                <Route
                  exact
                  path="/workspace/access-denied"
                  component={AccessDenied}
                />

                <Route path="/workspace/" component={WorkspaceLayout} />
              </Switch>

              {Object.keys(identity).length > 0 && (
                <>
                  <Route path="/workspace" component={SearchParamWatcher} />

                  <div className="App__workspace-content">
                    <div className="App__workspace-page">
                      <AISuggests />
                      <WorkspaceErrorBoundary>
                        <Switch>
                          {activeWorkspaceUuid?.length && !showOnboarding && (
                            <Redirect
                              exact
                              from="/workspace/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"
                              to={`/workspace/${activeWorkspaceUuid}/homepage`}
                            />
                          )}
                          <Route
                            path="/workspace/:workspaceUuid/campaign-creator-search"
                            component={CampaignCreatorSearch}
                          />
                          <Route
                            path="/workspace/:workspaceUuid/activity"
                            component={Activity}
                          />
                          <Redirect
                            from="/workspace/:workspaceUuid/get-started"
                            to="/workspace/:workspaceUuid/homepage"
                          />
                          <Route
                            path="/workspace/:workspaceUuid/homepage"
                            component={Homepage}
                          />
                          <Route
                            path="/workspace/:workspaceUuid/coupon-groups"
                            component={CouponGroups}
                          />
                          <Route
                            path="/workspace/:workspaceUuid/my-actions/:projectId?"
                            component={MyTasks}
                          />
                          <Route
                            path="/workspace/:workspaceUuid/projects"
                            exact
                            component={Project}
                          />

                          {websocket && (
                            <Route
                              path="/workspace/:workspaceUuid/projects/:projectId/:taskId?"
                              render={({ match }) => (
                                <SingleProject key={match.params.projectId} />
                              )}
                            />
                          )}

                          <Route
                            path="/workspace/:workspaceUuid/finance"
                            component={OnboardingPage}
                          />
                          {hasAccessToCreatorDiscovery && (
                            <Route
                              path="/workspace/:workspaceUuid/creator-discovery-tool"
                              component={CreatorDiscoveryTool}
                            />
                          )}

                          <Route
                            path="/workspace/:workspaceUuid/hashtag-live-search"
                            component={HashtagLiveSearch}
                          />

                          {hasAccessToCreatorDatabase && (
                            <Route
                              path="/workspace/:workspaceUuid/creator-database"
                              component={CreatorDatabase}
                            />
                          )}

                          <Route
                            path="/workspace/creator-showcase"
                            exact
                            component={CreatorShowcase}
                          />

                          <Route
                            path="/workspace/:workspaceUuid/report-generator"
                            component={ReportGenerator}
                          />

                          {/* Finance Reporting module - "Reporting" to clients.
                              Self-contained, see app/FinanceReporting/README.md.
                              The workspace allowlist is enforced by the API, which
                              answers 404 where the module is not switched on. */}
                          {hasAccessToFinanceReporting && (
                            <Route
                              path="/workspace/:workspaceUuid/reporting"
                              render={() => (
                                <FinanceReporting
                                  canConfigure={canConfigureFinanceReporting}
                                />
                              )}
                            />
                          )}

                          <Route
                            path="/workspace/:workspaceUuid/summary/:projectId?"
                            component={WorkspaceTasksSummary}
                          />

                          <Route
                            path="/workspace/:workspaceUuid/social-insights/:dashboardId?"
                            component={SocialInsights}
                          />

                          {hasAccessToDictionaries && (
                            <Route
                              path="/workspace/:workspaceUuid/dict/:wsDictionaryUrl/:dictionaryUuid"
                              exact
                              render={({ match }) => (
                                <Dictionary
                                  key={match.params.dictionaryUuid}
                                  {...match.params}
                                />
                              )}
                            />
                          )}
                          {/* <Route path="*" component={NoMatch} /> */}
                        </Switch>
                      </WorkspaceErrorBoundary>
                    </div>
                    <AIChat />
                  </div>
                </>
              )}

              <Route path="/workspace" component={MessageListener} />

              <Route
                path="/workspace/:workspaceUuid"
                component={WorkspaceCatcher}
              />

              <CreateNewProjectPane />
              <SearchHistoryPane />
            </main>

            <OutsideClickWarningModal />

            <div className="Overlays" ref={overlaysRef} />

            <ToastContainer
              position="top-right"
              draggable={false}
              className="ws-toast"
            />

            {wsProjectReportShowcasePdfReadyModalData && (
              <WsProjectReportShowcasePdfReadyModal
                reportUrl={wsProjectReportShowcasePdfReadyModalData?.reportUrl}
                closePdfReadyModal={() => dispatch(setPdfReadyModalData(null))}
              />
            )}
          </>
        </Providers>
      </Router>
    </div>
  );
}

export default App;
